import React, { useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/sharp-solid-svg-icons/faBolt';
import { faCheck } from '@fortawesome/sharp-solid-svg-icons/faCheck';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';

import style from './labs-confirmation-modal.module.less';
import ModalPortal from '../../../../../components/modal-portal';
import { CTA_ACTION_PREFIX, LABS_CTA_ACTION_EVENT } from '../../labs-feature-constants';
import { featureMessages } from '../../labs-feature-messages';
import { messages } from '../../messages';
/**
 * Labs modal component for confirming feature enable/disable actions
 * @param {object} props - Component props
 * @param {import('../../typedefs').LabsFeature} props.feature - The feature being confirmed
 * @param {boolean} [props.isEnabling] - Whether enabling (true) or disabling (false)
 * @param {boolean} [props.isEnableSuccess] - Whether the feature was just successfully enabled
 * @param {boolean} [props.isViewOnly] - Whether in view-only mode (no enable/disable action)
 * @param {() => void} [props.onConfirm] - Function called when user confirms
 * @param {() => void} props.onCancel - Function called when user cancels
 * @param {boolean} [props.isLoading] - Whether API call is in progress
 * @returns {React.ReactElement|null} The confirmation modal component
 */
export const LabsConfirmationModal = ({
  feature,
  isEnabling,
  isEnableSuccess = false,
  isViewOnly = false,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const { titleKey, confirmationTextKey, ctaButtonTitleKey, ctaRoute } = feature ?? {};
  const navigate = useNavigate();

  const confirmButtonTheme = isEnabling ? THEME_CONSTANTS.upsell : THEME_CONSTANTS.danger;

  const handleCtaClick = useCallback(() => {
    if (!ctaRoute) {
      return;
    }

    onCancel();

    if (ctaRoute.startsWith(CTA_ACTION_PREFIX)) {
      const actionId = ctaRoute.slice(CTA_ACTION_PREFIX.length);
      window.dispatchEvent(new CustomEvent(LABS_CTA_ACTION_EVENT, { detail: { actionId } }));
    } else {
      navigate(ctaRoute);
    }
  }, [ctaRoute, navigate, onCancel]);

  const confirmButtonContent = useMemo(() => {
    if (isEnabling) {
      return (
        <>
          <FontAwesomeIcon className={style['upsell-icon']} icon={faBolt} />
          <Text
            formattedMessageValues={{
              featureName: <FormattedMessage {...featureMessages[titleKey]} />,
            }}
            textType={TextType.Label}
            value={messages.activateButtonText}
          />
        </>
      );
    }

    return <Text textType={TextType.Label} value={messages.disable} />;
  }, [isEnabling, titleKey]);

  const hasCtaAction = ctaButtonTitleKey && ctaRoute;

  return (
    <ModalPortal onClose={onCancel}>
      <Modal className={style.labsConfirmationModal} size='large'>
        <ModalHeader>
          <Text textType={TextType.H2} value={featureMessages[titleKey]} />
        </ModalHeader>

        <ModalBody className={style.modalBody}>
          <FlexColumn className={style.contentWrapper}>
            <div className={style.scrollableContent}>
              {confirmationTextKey ? (
                <div className={style.featureConfirmationText}>
                  <Text textType={TextType.Paragraph} value={featureMessages[confirmationTextKey]} />
                </div>
              ) : null}

              {!isEnabling && !isViewOnly && !confirmationTextKey && (
                <div className={style.disableConfirmationText}>
                  <Text
                    formattedMessageValues={{
                      featureName: <FormattedMessage {...featureMessages[titleKey]} />,
                    }}
                    textType={TextType.Paragraph}
                    value={messages.disableConfirmation}
                  />
                </div>
              )}
            </div>
          </FlexColumn>
        </ModalBody>

        <ModalFooter className={style.modalFooter}>
          {isLoading ? (
            <FlexRow className={style.loadingContainer}>
              <LoadingIndicator size='small' />
            </FlexRow>
          ) : null}

          <FlexRow className={style.footerRow}>
            {isEnableSuccess ? (
              <FlexRow className={style.successMessage} gap={Gap.Space8}>
                <FontAwesomeIcon className={style.successIcon} icon={faCheck} />
                <Text textType={TextType.Label} value={messages.featureEnabledInline} />
              </FlexRow>
            ) : null}

            <FlexRow className={style.buttonContainer}>
              <Button disabled={!isViewOnly && isLoading} onClick={onCancel} theme={THEME_CONSTANTS.tertiary}>
                <Text textType={TextType.Label} value={isViewOnly ? messages.close : messages.cancel} />
              </Button>
              {isViewOnly && hasCtaAction ? (
                <Button onClick={handleCtaClick} theme={THEME_CONSTANTS.primary}>
                  <FormattedMessage {...featureMessages[ctaButtonTitleKey]} />
                </Button>
              ) : null}
              {!isViewOnly ? (
                <Button disabled={isLoading} onClick={onConfirm} theme={confirmButtonTheme}>
                  {confirmButtonContent}
                </Button>
              ) : null}
            </FlexRow>
          </FlexRow>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};
